import React, {useEffect, useState} from 'react';
import {useDispatch} from 'react-redux';

import {Variant} from '../../../api/models/variant';
import CreatokenSVGComponent from '../../../assets/icons/creatoken.svg';
import {fetchFeaturedVariants} from '../../../redux';
import {BasicCardVariants} from '../../../utils/_enums/basic-card-variants.enum';
import {SalesType} from '../../../utils/_enums/sales-type';
import {StrapiHeadlineElements} from '../../../utils/_enums/strapi-headline-elements.enum';
import {StrapiHeadlineStyles} from '../../../utils/_enums/strapi-headline-styles.enum';
import {TypographyVariantsEnum} from '../../../utils/_enums/typeography-variants.enum';
import {creatokenPosterUrlResolver} from '../../../utils/_helper/creatoken-posterUrl-resolver';
import {EnumToLanguageMapper} from '../../../utils/_helper/enum-to-language-mapper';
import {generateUrlToDetailedPage} from '../../../utils/_helper/generate-url-to-details-page-helper';
import {mapStrapiHeadline} from '../../../utils/_helper/strapi-mapper';
import {isDateInPast} from '../../../utils/_helper/time-helper';
import {useAppSelector} from '../../../utils/_hooks/hooks';
import {PaginationModel} from '../../../utils/_models/pagination-model';
import BasicCard from '../../_shared/basic-card/basic-card';
import {ButtonProps} from '../../_shared/button/button';
import CarouselComponent from '../../_shared/carousel/carousel';
import {Props as StageImageButtonTokenProps,} from '../../_shared/stage/stage-image-button-token/stage-image-button-token';
import StageImageBackground from '../../_shared/stage-image-background/stage-image-background';
import {Typography} from '../../_shared/typography/typography';
import VariantCard from '../../_shared/variant-card/variant-card';
import styles from './digital-originals-stage.module.scss';

export interface Props {
    title: string;
    titleVariant?: StrapiHeadlineStyles;
    titleElement?: StrapiHeadlineElements;
    background?: string;
    decButton?: ButtonProps | StageImageButtonTokenProps;
    inverted?: boolean;
}

export const DigitalOriginalsStage: React.FC<Props> = ({
                                                           title,
                                                           titleVariant,
                                                           titleElement,
                                                           background,
                                                           inverted
                                                       }) => {
    const variants = useAppSelector<PaginationModel<Variant[]>>(
        (state) => state.productSlice.featuredVariants
    );
    const [filteredVariants, setFilteredVariants] = useState([]);
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(fetchFeaturedVariants());
    }, []);

    useEffect(() => {
        if(variants?.data) {
            setFilteredVariants(variants?.data?.filter(variant => variant.displayedWhenFeatured === true));
        }
    }, [variants])

    return (
        <div className={styles['stage']}>
            <StageImageBackground url={background}/>
            <div className={styles['stage-content']}>
                {title
                    ? <div className={styles['title']}>
                        <Typography
                            variant={
                                titleVariant
                                    ? mapStrapiHeadline(titleVariant)
                                    : TypographyVariantsEnum.HEADING1
                            }
                            element={titleElement
                                ? titleElement
                                : 'h1'}
                            className={inverted ? styles['inverted'] : ''}
                        >
                            {title}
                        </Typography>
                    </div>
                    : undefined}
                {
                    filteredVariants.length === 0 ?
                        <CarouselComponent
                            type={BasicCardVariants.BASIC_CARD_HORIZONTAL_SMALL}
                            items={variants?.data?.map?.(variant => {
                                return <BasicCard
                                    key={variant.id}
                                    media={{
                                        mediaUrl: variant.coverArt?.url,
                                        initialLoad: true,
                                        contentType: variant.coverArt?.contentContentType,
                                        profilePictureUrl:
                                            variant.originatorsIds && variant.originatorsIds.length > 0
                                                ? variant.originatorsIds[0]
                                                : undefined,
                                        posterUrl: creatokenPosterUrlResolver(variant.id ?? -1),
                                        isTeaser: true
                                    }}
                                    category={EnumToLanguageMapper.categoryIdToCategoryEnum(variant.categoryId)}
                                    type={BasicCardVariants.BASIC_CARD_HORIZONTAL_SMALL}
                                    redirectUrl={generateUrlToDetailedPage(
                                        variant.productName,
                                        variant.title,
                                        variant.productId,
                                        variant.id)}
                                >
                                    <VariantCard
                                        variantId={variant.id}
                                        text={{
                                            headline: variant.title,
                                            topline: variant.originatorsNames ? variant.originatorsNames[0] : '',
                                            description: variant.variantDescriptionShort
                                        }}
                                        footer={{
                                            onPrimaryAction: () => undefined,
                                            actionType: variant.purchasableInstances > 0
                                            || (variant.salesAttribute?.salesType === SalesType.T_DROP
                                                && !isDateInPast(variant.salesAttribute?.saleTo)
                                                && isDateInPast(variant.salesAttribute?.saleFrom))
                                                ? 'INFORMATION_AND_BUY'
                                                : 'INFORMATION'
                                        }}
                                        type={BasicCardVariants.BASIC_CARD_HORIZONTAL_SMALL}
                                        redirectUrl={generateUrlToDetailedPage(
                                            variant.productName,
                                            variant.title,
                                            variant.productId,
                                            variant.id)}
                                    />
                                </BasicCard>
                            }) ?? []}
                        /> :
                        <CarouselComponent
                            type={BasicCardVariants.BASIC_CARD_HORIZONTAL_SMALL}
                            items={filteredVariants?.map?.(variant => {
                                return <BasicCard
                                    key={variant.id}
                                    media={{
                                        mediaUrl: variant.coverArt?.url,
                                        initialLoad: true,
                                        contentType: variant.coverArt?.contentContentType,
                                        profilePictureUrl:
                                            variant.originatorsIds && variant.originatorsIds.length > 0
                                                ? variant.originatorsIds[0]
                                                : undefined,
                                        posterUrl: creatokenPosterUrlResolver(variant.id ?? -1),
                                        isTeaser: true
                                    }}
                                    category={EnumToLanguageMapper.categoryIdToCategoryEnum(variant.categoryId)}
                                    type={BasicCardVariants.BASIC_CARD_HORIZONTAL_SMALL}
                                    redirectUrl={generateUrlToDetailedPage(
                                        variant.productName,
                                        variant.title,
                                        variant.productId,
                                        variant.id)}
                                >
                                    <VariantCard
                                        variantId={variant.id}
                                        text={{
                                            headline: variant.title,
                                            topline: variant.originatorsNames ? variant.originatorsNames[0] : '',
                                            description: variant.variantDescriptionShort
                                        }}
                                        footer={{
                                            onPrimaryAction: () => undefined,
                                            actionType: variant.purchasableInstances > 0
                                            || (variant.salesAttribute?.salesType === SalesType.T_DROP
                                                && !isDateInPast(variant.salesAttribute?.saleTo)
                                                && isDateInPast(variant.salesAttribute?.saleFrom))
                                                ? 'INFORMATION_AND_BUY'
                                                : 'INFORMATION'
                                        }}
                                        type={BasicCardVariants.BASIC_CARD_HORIZONTAL_SMALL}
                                        redirectUrl={generateUrlToDetailedPage(
                                            variant.productName,
                                            variant.title,
                                            variant.productId,
                                            variant.id)}
                                    />
                                </BasicCard>
                            }) ?? []}
                        />
                }
            </div>
            <div>
                <div className={styles.decButtonContainer}>
                    <CreatokenSVGComponent/>
                </div>
            </div>
        </div>
    );
};
